import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { captureUtms } from "@/lib/utils";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import WelcomePickCase from "@/pages/WelcomePickCase";
import Pricing from "@/pages/Pricing";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import Dashboard from "@/pages/Dashboard";
import CasesPage from "@/pages/CasesPage";
import EventsPage from "@/pages/EventsPage";
import ProfilePage from "@/pages/ProfilePage";
import CaseWorkspace from "@/pages/CaseWorkspace";
import EvidenceBoardPage from "@/pages/EvidenceBoardPage";
import Calendar from "@/pages/Calendar";
import ThreadPage from "@/pages/ThreadPage";
import AdminPage from "@/pages/AdminPage";
import MembersPage from "@/pages/MembersPage";
import ShopPage from "@/pages/ShopPage";
import AffiliatePage from "@/pages/AffiliatePage";
import MoveYourDnaPage from "@/pages/MoveYourDnaPage";
import AcademyPage from "@/pages/AcademyPage";
import CoursePage from "@/pages/CoursePage";
import AcademyModulePage from "@/pages/AcademyModulePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DLHome from "@/pages/dl/DLHome";
import DLLayout from "@/pages/dl/DLLayout";
import DLCaseHQ from "@/pages/dl/DLCaseHQ";
import DLTeam from "@/pages/dl/DLTeam";
import DLCaseRecord from "@/pages/dl/DLCaseRecord";
import DLFilmRoom from "@/pages/dl/DLFilmRoom";
import DLPlaybook from "@/pages/dl/DLPlaybook";
import DLResources from "@/pages/dl/DLResources";
import DLLockedPlays from "@/pages/dl/DLLockedPlays";
import DLStandings from "@/pages/dl/DLStandings";
import ContactPage from "@/pages/ContactPage";
import VideoLibraryPage from "@/pages/VideoLibraryPage";
import CommunityManagerChat, { ChatProvider } from "@/components/CommunityManagerChat";

function AuthCallback() {
  const navigate = useNavigate();
  const { exchangeGoogle } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sid) { navigate("/login"); return; }
    exchangeGoogle(sid)
      .then(() => navigate("/dashboard", { replace: true }))
      .catch(() => navigate("/login", { replace: true }));
  }, [exchangeGoogle, navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-charcoal">
      <div className="label-mono animate-pulse">Securing session…</div>
    </div>
  );
}

function ProtectedRoute({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-charcoal"><div className="label-mono animate-pulse">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function MemberRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-charcoal"><div className="label-mono animate-pulse">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  const isMember = user.role === "admin" || user.subscription_status === "active";
  if (!isMember) return <Navigate to="/pricing" replace />;
  return children;
}

function AcademyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-charcoal"><div className="label-mono animate-pulse">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.academy_ok) return <Navigate to="/course" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { captureUtms(); }, [location.search]);
  useEffect(() => {
    let vid = "";
    try {
      vid = localStorage.getItem("hstc_visitor_id") || "";
      if (!vid) {
        vid = (window.crypto?.randomUUID?.() || `v_${Date.now()}_${Math.random().toString(36).slice(2)}`);
        localStorage.setItem("hstc_visitor_id", vid);
      }
    } catch { /* localStorage unavailable */ }
    api.post("/track/visit", { path: location.pathname, visitor_id: vid }).catch(() => {});
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<AuthPage mode="login" />} />
      <Route path="/register" element={<AuthPage mode="register" />} />
      <Route path="/welcome" element={<ProtectedRoute><WelcomePickCase /></ProtectedRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/cases" element={<CasesPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/course" element={<CoursePage />} />
      <Route path="/academy" element={<AcademyRoute><AcademyPage /></AcademyRoute>} />
      <Route path="/academy/:moduleId" element={<AcademyRoute><AcademyModulePage /></AcademyRoute>} />
      <Route path="/u/:userId" element={<MemberRoute><ProfilePage /></MemberRoute>} />
      <Route path="/cases/:slug" element={<CaseWorkspace />} />
      <Route path="/cases/:slug/board" element={<MemberRoute><EvidenceBoardPage /></MemberRoute>} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/affiliate" element={<AffiliatePage />} />
      <Route path="/move-your-dna" element={<MoveYourDnaPage />} />
      <Route path="/calendar" element={<MemberRoute><Calendar /></MemberRoute>} />
      <Route path="/threads/:threadId" element={<MemberRoute><ThreadPage /></MemberRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute adminOnly><MembersPage /></ProtectedRoute>} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/library" element={<VideoLibraryPage />} />
      <Route path="/defense-league" element={<MemberRoute><DLHome /></MemberRoute>} />
      <Route path="/defense-league/cases/:caseId" element={<MemberRoute><DLLayout /></MemberRoute>}>
        <Route index element={<DLCaseHQ />} />
        <Route path="team" element={<DLTeam />} />
        <Route path="record" element={<DLCaseRecord />} />
        <Route path="film" element={<DLFilmRoom />} />
        <Route path="playbook" element={<DLPlaybook />} />
        <Route path="resources" element={<DLResources />} />
        <Route path="plays" element={<DLLockedPlays />} />
        <Route path="standings" element={<DLStandings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ChatProvider>
            <Toaster theme="dark" position="top-right" richColors />
            <AppRouter />
            <CommunityManagerChat />
          </ChatProvider>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}
